import dayjs from "dayjs";
import { SyncJobStatus } from "@boxem/core-api-schema";

import styles from "./fba-shipment-list-item.module.css";

import { FbaBatchItem } from "@/contexts/ShipmentContext";
import ProductImage from "@/components/ProductImage/ProductImage";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconCheck from "@/components/icons/IconCheck";
import IconError from "@/components/icons/IconError";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Button from "@/components/forms/Button/Button";
import IconDelete from "@/components/icons/iconDelete";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";
import { productConditionToString } from "@/app/fba/utils";

type FbaShipmentListItemProps = {
  item: FbaBatchItem;
  onEditItemClicked: (item: FbaBatchItem) => void;
  onDeleteItemClicked?: (item: FbaBatchItem) => void;
}

const FbaShipmentListItem = ({
  item,
  onEditItemClicked,
  onDeleteItemClicked,
}: FbaShipmentListItemProps) => {
  return (
    <>
      <div className={styles.container} onClick={() => onEditItemClicked(item)}>
        <Flex className={styles.productInfo} gap={2}>
          <Flex direction="column" align="center">
            <ProductImage className={styles.productImage} imageUrl={item.image} alt={item.name} />
            <Flex>
              <span className="small-text weak">x</span>
              <span className="small-text strong">{item.quantity}</span>
            </Flex>
            {onDeleteItemClicked && (
              <Button
                type="text"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteItemClicked?.(item);
                }}
              >
                <IconDelete size={24} />
              </Button>
            )}
          </Flex>

          <Flex className={styles.minWidth0} direction="column" gap={3}>
            <Flex className={styles.minWidth0 + " " + styles.itemListContent} direction="column">
              <Flex align="center">
                <Tooltip title={item.name}>
                  <div className={styles.itemName}>{item.name}</div>
                </Tooltip>
              </Flex>

              <CopyToClipBoard value={item.asin} tooltipText="Copy ASIN" tooltipPlacement="bottom-left">
                <Flex gap={1} className={styles.listItemRow}>
                  <span className="small-text weak">ASIN:</span>
                  <span className="small-text strong">{item.asin}</span>
                </Flex>
              </CopyToClipBoard>

              <CopyToClipBoard value={item.sku} tooltipText="Copy SKU" tooltipPlacement="bottom-left">
                <Flex gap={1} className={styles.listItemRow}>
                  <span className="small-text weak">SKU:</span>
                  <span className="small-text strong">{item.sku}</span>
                </Flex>
              </CopyToClipBoard>
              <Flex gap={1} className={styles.listItemRow}>
                <span className="small-text weak">Condition:</span>
                <span className="small-text strong">{productConditionToString(item.condition)}</span>
              </Flex>
              {!!item.expirationDate && (
                <Flex gap={1} className={styles.listItemRow}>
                  <span className="small-text weak">Expiration:</span>
                  <span className="small-text strong">{dayjs.utc(item.expirationDate).format("MM/DD/YYYY")}</span>
                </Flex>
              )}
              {!!item.prepCategoryPollingStatus && (
                <Flex gap={1} className={styles.listItemRow}>
                  
                  {(() => {
                    switch (item.prepCategoryPollingStatus) {
                      case SyncJobStatus.COMPLETED:
                        return (
                          <Flex align="center" gap={1}>
                            <span className="small-text weak">Prep category set&nbsp;</span><IconCheck/>
                          </Flex>
                        );

                      case SyncJobStatus.PROCESSING:
                      case SyncJobStatus.PENDING:
                        return (
                          <Flex align="center" gap={1}>
                            <span className="small-text weak">Prep category updating...&nbsp;</span><LoadingSpinner size="1rem"/>
                          </Flex>
                        );

                      case SyncJobStatus.TERMINATED_WITH_ERROR:
                        return (
                          <Flex align="center" gap={1}>
                            <span className="small-text weak">Prep category error&nbsp;</span><IconError/>
                          </Flex>
                        );
                    }
                  })()}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </div>
    </>
  );
};

export default FbaShipmentListItem;
